import { useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
} from "web-design-system";
import { usePortfolioJourney, useReviewBenchmarks } from "creso-core/hooks";
import {
  PortfolioJourney as PortfolioJourneyData,
  PortfolioJourneyView,
  PortfolioReviewSource,
} from "creso-core/types";
import { formatAsCrore, formatAsPercentage } from "creso-core/utils";
import { DEFAULT_REVIEW_BENCHMARK } from "../constants";
import {
  ReviewBlockState,
  ReviewNotice,
  ReviewSection,
} from "./review-section";

const chartConfig = {
  current: { label: "Current portfolio", color: "var(--color-chart-1)" },
  benchmark: { label: "Benchmark", color: "var(--color-muted-foreground)" },
  net_flow: { label: "Net flow", color: "var(--color-chart-1)" },
} satisfies ChartConfig;

const formatAxisAmount = (value: number) => formatAsCrore(value, 0, true);

const formatTooltipAmount = (value: unknown) => formatAsCrore(Number(value));

export function PortfolioJourney({
  investmentAccountId,
  source,
}: {
  investmentAccountId: string;
  source: PortfolioReviewSource;
}) {
  const [view, setView] = useState<PortfolioJourneyView>("returns");
  const [benchmark, setBenchmark] = useState(DEFAULT_REVIEW_BENCHMARK);

  const { data: benchmarks } = useReviewBenchmarks();
  const { data, isLoading, isError } = usePortfolioJourney({
    investment_account_guid: investmentAccountId,
    source,
    benchmark,
    view,
  });

  const benchmarkName = data?.benchmark_name ?? benchmark;
  const { current, benchmark: benchmarkMetrics } = data?.metrics ?? {};
  const hasSeries = !!data?.series.current.length;

  return (
    <ReviewSection title="Portfolio journey" defaultOpen>
      <ReviewBlockState
        isLoading={isLoading}
        isError={isError}
        isEmpty={!!data && !hasSeries}
        emptyMessage="No transactions found to build the portfolio journey."
      >
        {current && benchmarkMetrics ? (
          <PerformanceCallout
            current={current.xirr}
            benchmark={benchmarkMetrics.xirr}
          />
        ) : null}

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Tabs
              value={view}
              onValueChange={(v) => setView(v as PortfolioJourneyView)}
            >
              <TabsList>
                <TabsTrigger value="returns">Returns backtest</TabsTrigger>
                <TabsTrigger value="flows">Quarterly flows</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex flex-wrap items-center gap-4">
              <Select value={benchmark} onValueChange={setBenchmark}>
                <SelectTrigger
                  className="w-44 bg-background"
                  aria-label="Benchmark"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(benchmarks?.length
                    ? benchmarks.map((b) => b.name)
                    : [DEFAULT_REVIEW_BENCHMARK]
                  ).map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {data && view === "flows" ? (
            <QuarterlyFlowsChart data={data} />
          ) : null}
          {data && view === "returns" ? (
            <>
              <ReturnsChart data={data} />
              <JourneyLegend benchmarkName={benchmarkName} />
            </>
          ) : null}
          {/* {data?.window_clipped && data.window_start ? (
            <span className="text-center text-xs text-muted-foreground">
              Journey starts{" "}
              {format(new Date(data.window_start), "d MMM, yyyy")}, the earliest
              date every fund has NAV history.
            </span>
          ) : null} */}
        </div>

        {current && benchmarkMetrics ? (
          <JourneyMetricsTable
            benchmarkName={benchmarkName}
            rows={[
              {
                label: "Total invested",
                current: formatAsCrore(current.total_invested),
                benchmark: formatAsCrore(benchmarkMetrics.total_invested),
              },
              {
                label: "Final value",
                current: formatAsCrore(current.final_value),
                benchmark: formatAsCrore(benchmarkMetrics.final_value),
              },
              {
                label: "XIRR",
                current: <SignedPercent value={current.xirr} />,
                benchmark: <SignedPercent value={benchmarkMetrics.xirr} />,
              },
            ]}
          />
        ) : null}
      </ReviewBlockState>
    </ReviewSection>
  );
}

function PerformanceCallout({
  current,
  benchmark,
}: {
  current: string;
  benchmark: string;
}) {
  const diff = Number(current) - Number(benchmark);
  return (
    <ReviewNotice>
      Portfolio {diff >= 0 ? "outperformed" : "underperformed"} benchmark by{" "}
      {formatAsPercentage(Math.abs(diff))}
    </ReviewNotice>
  );
}

export function SignedPercent({ value }: { value: string }) {
  const num = Number(value);
  return (
    <span className={num >= 0 ? "text-constructive" : "text-destructive"}>
      {num > 0 ? "+" : ""}
      {formatAsPercentage(num, "-", 1)}
    </span>
  );
}

export function ReturnsChart({
  data,
  grid = true,
  className,
}: {
  data: Pick<PortfolioJourneyData, "series">;
  grid?: boolean;
  className?: string;
}) {
  const points = useMemo(() => {
    const benchmarkByDate = new Map(
      data.series.benchmark.map((p) => [p.date, Number(p.value)])
    );
    return data.series.current.map((p) => ({
      date: p.date,
      current: Number(p.value),
      benchmark: benchmarkByDate.get(p.date),
    }));
  }, [data]);

  return (
    <ChartContainer
      config={chartConfig}
      className={cn("aspect-auto h-80 w-full", className)}
    >
      <ComposedChart data={points} margin={{ top: 16, right: 8, left: 8 }}>
        <defs>
          <linearGradient id="journey-current-fill" x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="0%"
              stopColor="var(--color-current)"
              stopOpacity={0.12}
            />
            <stop
              offset="100%"
              stopColor="var(--color-current)"
              stopOpacity={0}
            />
          </linearGradient>
        </defs>
        {grid ? <CartesianGrid vertical={false} strokeOpacity={0.4} /> : null}
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          minTickGap={32}
          tickFormatter={(d: string) => format(new Date(d), "MMM yy")}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          width={48}
          tickFormatter={formatAxisAmount}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(_, payload) =>
                format(new Date(payload[0]?.payload.date), "d MMM, yyyy")
              }
              valueFormatter={formatTooltipAmount}
            />
          }
        />
        <Area
          dataKey="current"
          type="monotone"
          stroke="var(--color-current)"
          strokeWidth={2}
          fill="url(#journey-current-fill)"
          dot={false}
          isAnimationActive={false}
        />
        <Line
          dataKey="benchmark"
          type="monotone"
          stroke="var(--color-benchmark)"
          strokeWidth={2}
          strokeDasharray="2 3"
          dot={false}
          connectNulls
          isAnimationActive={false}
        />
      </ComposedChart>
    </ChartContainer>
  );
}

function QuarterlyFlowsChart({ data }: { data: PortfolioJourneyData }) {
  const points = useMemo(
    () =>
      (data.quarterly_flows ?? []).map((f) => ({
        quarter: `Q${f.quarter} ${String(f.year).slice(2)}`,
        net_flow: Number(f.net_flow),
      })),
    [data]
  );

  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-80 w-full">
      <BarChart data={points} margin={{ top: 16, right: 8, left: 8 }}>
        <CartesianGrid vertical={false} strokeOpacity={0.4} />
        <XAxis
          dataKey="quarter"
          tickLine={false}
          axisLine={false}
          minTickGap={16}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          width={48}
          tickFormatter={formatAxisAmount}
        />
        <ChartTooltip
          content={<ChartTooltipContent valueFormatter={formatTooltipAmount} />}
        />
        <Bar dataKey="net_flow" radius={4} isAnimationActive={false}>
          {points.map((p) => (
            <Cell
              key={p.quarter}
              fill={
                p.net_flow >= 0
                  ? "var(--color-chart-1)"
                  : "var(--color-chart-3)"
              }
            />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

function JourneyLegend({ benchmarkName }: { benchmarkName: string }) {
  return (
    <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
      <span className="flex items-center gap-1">
        <CurrentMarker />
        Current portfolio
      </span>
      <span className="flex items-center gap-1">
        <BenchmarkMarker />
        {benchmarkName}
      </span>
    </div>
  );
}

const CurrentMarker = () => <span className="size-3 rounded-xs bg-chart-1" />;

const BenchmarkMarker = () => (
  <span className="w-5 border-t-4 border-dotted border-muted-foreground" />
);

export function JourneyMetricsTable({
  benchmarkName,
  caption,
  markers,
  rows,
}: {
  benchmarkName: string;
  // Heading over the row labels.
  caption?: string;
  // Series markers beside the column headings, for when there's no legend.
  markers?: boolean;
  rows: {
    label: string;
    current: React.ReactNode;
    benchmark: React.ReactNode;
  }[];
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-120 text-sm">
        <thead className="bg-card">
          <tr>
            <th className="w-1/3 p-4 text-left font-semibold">{caption}</th>
            <th className="p-4 text-left font-semibold">
              <span className="flex items-center gap-2">
                {markers ? <CurrentMarker /> : null}
                Current portfolio
              </span>
            </th>
            <th className="p-4 text-left font-semibold">
              <span className="flex items-center gap-2">
                {markers ? <BenchmarkMarker /> : null}
                {benchmarkName}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.label}
              className={cn(index > 0 && "border-t border-border")}
            >
              <td className="px-4 py-3 text-muted-foreground">{row.label}</td>
              <td className="px-4 py-3">{row.current}</td>
              <td className="px-4 py-3">{row.benchmark}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
